import { useCallback, useState } from "react";
import type { Locale } from "../../shared/dto";
import type { StaffRun } from "./model";
import {
  fillClient,
  type ActionState,
  type ClientCreationSummary
} from "./AdminImportsShared";

// "Založit klienta" used to answer with the console's generic "Uloženo" and
// nothing else: the admin could not tell which client had been created, who
// owned it, which partner got it or whether anything started running. The
// confirmation card reads the result back from the database and names all of
// it, with one button that opens the new client.

export interface ClientCreationValues {
  name: string;
  ownerEmail: string;
  ownerDisplayName: string;
  partnerId: string;
  marketLanguage: Locale;
  purchaseSource: string;
  instanceStartMode: string;
  /** F143: which journey the client walks - `full` or `sales_only`. */
  journeyMode: string;
  includesApp: boolean;
}

export interface ClientCreation {
  state: ActionState;
  summary: ClientCreationSummary | null;
  fallbackClientId: string | null;
  /** D13: the locale key of a NAMED refusal, so the strip stops printing the
   *  panel's generic "Klienta se nepodařilo založit" over a refusal that says
   *  exactly what is wrong. */
  errorKey: string | null;
  create: (values: ClientCreationValues) => Promise<boolean>;
  reset: () => void;
}

/** Server refusals of `POST /api/admin/clients` that have their own sentence. */
const CREATE_ERROR_KEYS: Record<string, string> = {
  owner_has_client: "staffConsole.adminImports.created.duplicateOwner"
};

export function useClientCreation(options: { run: StaffRun; refresh: () => Promise<void> }): ClientCreation {
  const [state, setState] = useState<ActionState>("idle");
  const [summary, setSummary] = useState<ClientCreationSummary | null>(null);
  const [fallbackClientId, setFallbackClientId] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const { run, refresh } = options;

  const create = useCallback(async (values: ClientCreationValues) => {
    setState("working");
    setSummary(null);
    setFallbackClientId(null);
    setErrorKey(null);
    const response = await run("/api/admin/clients", { method: "POST", body: values }, { refresh: false });
    if (!response.ok) {
      // D13: an address that already owns a client is a NAMED refusal, not a
      // failure. Without this the form answered "Klienta se nepodařilo
      // založit" and the admin's next move was to try again.
      let code = "";
      try {
        const body = await response.json() as { error?: string };
        if (typeof body.error === "string") code = body.error;
      } catch { /* the generic sentence covers an unreadable body */ }
      setErrorKey(CREATE_ERROR_KEYS[code] ?? null);
      setState("error");
      return false;
    }
    const body = await response.json().catch(() => ({})) as { clientId?: string };
    if (!body.clientId) {
      setState("error");
      return false;
    }
    try {
      const detail = await fetch(
        `/api/admin/imports/clients/${encodeURIComponent(body.clientId)}/summary`,
        { credentials: "include" }
      );
      if (!detail.ok) throw new Error("summary_failed");
      setSummary(await detail.json() as ClientCreationSummary);
    } catch {
      // The client exists either way; the card then claims only that much.
      setFallbackClientId(body.clientId);
    }
    setState("done");
    await refresh();
    return true;
  }, [refresh, run]);

  const reset = useCallback(() => {
    setState("idle");
    setSummary(null);
    setFallbackClientId(null);
    setErrorKey(null);
  }, []);

  return { state, summary, fallbackClientId, errorKey, create, reset };
}

export function AdminClientCreatedCard(props: {
  summary: ClientCreationSummary | null;
  fallbackClientId: string | null;
  t: (key: string) => string;
  onReset: () => void;
}) {
  const summary = props.summary;
  if (!summary) {
    if (!props.fallbackClientId) return null;
    return (
      <section className="admin-client-created partial" role="status">
        <p className="panel-label">{props.t("staffConsole.adminImports.created.kicker")}</p>
        <h4>{props.t("staffConsole.adminImports.created.title")}</h4>
        <p>{props.t("staffConsole.adminImports.created.detailsUnavailable")}</p>
        <div className="admin-client-created-actions">
          <a className="primary-button" href={`/?client=${encodeURIComponent(props.fallbackClientId)}`}>
            {props.t("staffConsole.adminImports.created.open")}
          </a>
          <button className="text-button" type="button" onClick={props.onReset}>
            {props.t("staffConsole.adminImports.created.another")}
          </button>
        </div>
      </section>
    );
  }
  return (
    <section className="admin-client-created" role="status">
      <div className="admin-client-created-identity">
        <p className="panel-label">{props.t("staffConsole.adminImports.created.kicker")}</p>
        <h4>{fillClient(props.t("staffConsole.adminImports.created.named"), summary.client.name)}</h4>
        <dl className="admin-client-created-facts">
          <dt>{props.t("staffConsole.adminImports.created.owner")}</dt>
          <dd>{[summary.owner.name, summary.owner.email].filter(Boolean).join(" · ")}</dd>
          <dt>{props.t("staffConsole.adminImports.created.partner")}</dt>
          <dd>
            {summary.partner
              ? [summary.partner.name, summary.partner.email].filter(Boolean).join(" · ")
              : props.t("staffConsole.adminImports.created.noPartner")}
          </dd>
        </dl>
      </div>
      <div className="admin-client-created-automations">
        <p className="panel-label">{props.t("staffConsole.adminImports.created.automations")}</p>
        <ul>
          {summary.automations.map((automation) => (
            <li key={automation.key} className={`automation-${automation.state}`}>
              <span>{props.t(`staffConsole.adminImports.automation.${automation.key}`)}</span>
              <span className={`admin-import-state ${automation.state}`}>
                {props.t(`staffConsole.adminImports.automationState.${automation.state}`)}
              </span>
            </li>
          ))}
        </ul>
      </div>
      {/* The two next steps close the card across its full width: they belong to
          the whole result, not to the automation column. */}
      <div className="admin-client-created-actions">
        <a className="primary-button" href={summary.deepLink}>
          {props.t("staffConsole.adminImports.created.open")}
        </a>
        <button className="text-button" type="button" onClick={props.onReset}>
          {props.t("staffConsole.adminImports.created.another")}
        </button>
      </div>
    </section>
  );
}
